import { useEffect } from "react";

export const SITE_URL = "https://ninjathleticlub.com";
export const ORG_ID = `${SITE_URL}/#org`;
export const WEBSITE_ID = `${SITE_URL}/#website`;
export const DEFAULT_OG = `${SITE_URL}/images/art-discipline-1200.webp`;

/* ---------- schema builders ---------- */

export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${SITE_URL}${it.path}`,
  })),
});

export const faqSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: items.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

export const articleSchema = (a) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: a.title,
  description: a.metaDescription,
  image: `${SITE_URL}${a.hero}`,
  datePublished: a.date,
  dateModified: a.date,
  author: { "@type": "Organization", name: "Ninja Athletic Club", url: SITE_URL },
  publisher: { "@id": ORG_ID },
  mainEntityOfPage: `${SITE_URL}/mindset/${a.slug}`,
});

export const productSchema = (p) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  name: p.title,
  image: [`${SITE_URL}${p.art}`, `${SITE_URL}${p.room}`],
  description: p.description,
  brand: { "@type": "Brand", name: "Ninja Athletic Club" },
  offers: {
    "@type": "AggregateOffer",
    priceCurrency: "USD",
    lowPrice: String(p.sizes[0].price),
    highPrice: String(p.sizes[p.sizes.length - 1].price),
    offerCount: String(p.sizes.length),
    availability: "https://schema.org/PreOrder",
    url: `${SITE_URL}/shop/${p.slug}`,
  },
});

/* ---------- head manager ---------- */

function setMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * Per-page SEO: title, description, canonical, OpenGraph, Twitter, JSON-LD.
 * Usage: <Seo title="..." description="..." path="/shop" image="/images/x.webp" type="website" schemas={[...]} noindex={false} />
 */
export default function Seo({ title, description, path = "/", image, type = "website", schemas = [], noindex = false }) {
  useEffect(() => {
    const url = `${SITE_URL}${path}`;
    const img = image ? (image.startsWith("http") ? image : `${SITE_URL}${image}`) : DEFAULT_OG;

    document.title = title;
    setMeta("name", "description", description);
    setMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large");
    setCanonical(url);

    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", url);
    setMeta("property", "og:type", type);
    setMeta("property", "og:image", img);
    setMeta("property", "og:site_name", "Ninja Athletic Club");

    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", img);

    // JSON-LD: remove previous page-level schemas, inject current ones
    document.querySelectorAll('script[data-seo="page"]').forEach((s) => s.remove());
    schemas.forEach((schema) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.dataset.seo = "page";
      s.textContent = JSON.stringify(schema);
      document.head.appendChild(s);
    });

    return () => {
      document.querySelectorAll('script[data-seo="page"]').forEach((s) => s.remove());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, path, image, type, noindex]);

  return null;
}
